/**
 * Хук для группировки посадок по культурам и локациям
 */
import { useMemo } from 'react';
import type { Planting } from '../../../models/Planting';
import { getLocationName, getPlantingDisplayQuantity } from '../utils/plantingsFormatters';

export interface LocationGroup {
  locationName: string;
  items: Planting[];
  count: number;
  totalQuantity: number;
}

export interface CropGroup {
  cropKey: string;
  cropId: number;
  cropName: string;
  cropLatinName: string | null;
  locations: LocationGroup[];
  totalCount: number;
  totalQuantity: number;
}

/**
 * Группирует посадки по культурам, затем по локациям
 */
export const usePlantingsGrouping = (plantings: Planting[]): CropGroup[] => {
  return useMemo(() => {
    const groups: Record<string, {
      cropId: number;
      cropName: string;
      cropLatinName: string | null;
      byLocation: Record<string, Planting[]>;
      totalCount: number;
      totalQuantity: number;
    }> = {};

    plantings.forEach((planting) => {
      const cropKey = String(planting.crop?.id || 'unknown');
      const cropName = planting.crop?.name || 'Без названия';
      const cropLatinName = planting.crop?.latinName || null;
      const locationKey = getLocationName(planting);
      
      if (!groups[cropKey]) {
        groups[cropKey] = {
          cropId: typeof planting.crop?.id === 'number' ? planting.crop.id : 0,
          cropName,
          cropLatinName,
          byLocation: {},
          totalCount: 0,
          totalQuantity: 0,
        };
      }
      
      if (!groups[cropKey].byLocation[locationKey]) {
        groups[cropKey].byLocation[locationKey] = [];
      }
      
      groups[cropKey].byLocation[locationKey].push(planting);
      groups[cropKey].totalCount++;
      groups[cropKey].totalQuantity += getPlantingDisplayQuantity(planting);
    });

    return Object.entries(groups)
      .map(([cropKey, data]) => ({
        cropKey,
        ...data,
        locations: Object.entries(data.byLocation)
          .map(([locationName, items]) => ({
            locationName,
            items,
            count: items.length,
            totalQuantity: items.reduce(
              (acc, p) => acc + getPlantingDisplayQuantity(p),
              0
            ),
          }))
          .sort((a, b) => a.locationName.localeCompare(b.locationName)),
      }))
      .sort((a, b) => a.cropName.localeCompare(b.cropName));
  }, [plantings]);
};
